// pages/mobile/RequestPage.js
// Service request dispatch (mockup 3c). WhatsApp only lets staff confirm a
// request, so there is no decline here; staff call dispatch instead.
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import AnswerBar from './AnswerBar';
import { DetailTopBar, LoadState, StatusPill } from './MobileChrome';
import { PinIcon } from './icons';
import { formatSent, mapsLink } from './format';
import { fetchDetail, useMobileData } from './mobileApi';
import styles from './mobile.module.css';
import useMessageRead from './useMessageRead';

const REQUEST_LABELS = {
  confirm: 'Confirm',
  deny: null,
  confirmedToast: 'Confirmed. Sent to dispatch.',
  confirmedState: "You're confirmed for this request.",
  deniedState: "You're confirmed for this request.",
  footnote: "Can't do it? Call dispatch.",
};

const RequestPage = () => {
  const { id } = useParams();
  const load = useCallback(() => fetchDetail('request', id), [id]);
  const { data: request, error, loading, reload } = useMobileData(load);
  useMessageRead(request);

  if (!request) {
    return (
      <main className={styles.page}>
        <DetailTopBar title="Service request" />
        <LoadState loading={loading} error={error} onRetry={reload} />
      </main>
    );
  }

  const location = mapsLink(request.latitude, request.longitude, request.address);
  const snowedge = request.snowedge;
  const closed = !request.needs_answer && !['confirmed', 'completed'].includes((request.status || '').toLowerCase());

  return (
    <main className={`${styles.page} ${closed ? '' : styles.pageWithActions}`}>
      <DetailTopBar title={`Request #${request.id}`} />
      <div className={styles.content}>
        <section className={styles.card} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className={styles.heroTop}>
            <span className={`${styles.statusPill} ${styles.iconRequest}`}>SERVICE REQUEST</span>
            <StatusPill status={request.status} needsAnswer={request.needs_answer} neutralLabel={request.status} />
          </div>
          <span className={styles.mutedText}>{request.staff_name}, you've been sent to</span>
          <h2 className={styles.heroTitle} style={{ fontSize: 24 }}>
            {[request.request_type, request.site_name].filter(Boolean).join(' · ')}
          </h2>
          {location && (
            <a href={location} target="_blank" rel="noreferrer" className={styles.banner} style={{ background: '#f4f6f4', border: 'none' }}>
              <span className={`${styles.tile} ${styles.iconRequest}`}><PinIcon size={20} /></span>
              <span>
                <strong>{request.address || 'Site location'}</strong>
                <br />
                Open in Maps
              </span>
            </a>
          )}
          <span className={styles.mutedText}>Sent {formatSent(request.sent_at)}</span>
        </section>

        {request.details && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>What the client reported</h2>
            <div className={styles.card}><p className={styles.bodyText}>{request.details}</p></div>
          </section>
        )}

        {snowedge && (
          <section className={styles.cardList} aria-label="SnowEdge details">
            <div className={styles.detailRow}><span>Contact</span><span>{snowedge.contact_name}</span></div>
            <div className={styles.detailRow}>
              <span>Phone</span>
              <span><a href={`tel:${snowedge.contact_phone}`}>{snowedge.contact_phone}</a></span>
            </div>
            <div className={styles.detailRow}>
              <span>Requested for</span>
              <span>{[snowedge.requested_date, snowedge.requested_time].filter(Boolean).join(' ')}</span>
            </div>
          </section>
        )}

        {request.photos.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Photos · {request.photos.length}</h2>
            <div className={styles.photoGrid}>
              {request.photos.map((url, index) => (
                <a key={url} href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={`From the client, ${index + 1} of ${request.photos.length}`} className={styles.photo} />
                </a>
              ))}
            </div>
          </section>
        )}

        {request.dispatch_notes && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Dispatch notes</h2>
            <div className={styles.card}><p className={styles.bodyText}>{request.dispatch_notes}</p></div>
          </section>
        )}
      </div>

      {!closed && (
        <AnswerBar
          kind="request"
          id={request.id}
          status={request.status}
          needsAnswer={request.needs_answer}
          canAnswer={request.can_answer}
          closedReason={request.closed_reason}
          labels={REQUEST_LABELS}
          allowChange={false}
          onChanged={reload}
        />
      )}
    </main>
  );
};

export default RequestPage;
